import type { AspectRatioToken, VariantSetDocument, VariantState } from './types';

/**
 * THE READY GATE (spec §6.9) — the single most important rule in the workbench.
 *
 * Four independent products marketed auto-resize as automatic and then publicly
 * walked it back; Pencil's own copy concedes "some formats will need to be
 * manually tweaked and refined". Encoding the review requirement in the
 * TRANSITION GATE rather than in a help string is what stops us joining them.
 *
 * `Ready` is refused unless EVERY variant in the set:
 *   1. has a generated image,
 *   2. is not currently generating,
 *   3. has `needsReview === false`.
 *
 * An empty set is refused too — "all zero variants pass" is a vacuous truth and
 * exactly the kind of silent no-op this repo keeps shipping.
 */

export type ReadyBlockerReason = 'no-variants' | 'no-image' | 'generating' | 'needs-review';

export interface ReadyBlocker {
  reason: ReadyBlockerReason;
  /** Null only for `no-variants`. Clicking the tooltip selects this variant. */
  variantId: string | null;
  aspectRatio: AspectRatioToken | null;
  /** The named blocker. Never a generic "not ready yet". */
  message: string;
}

export interface ReadyGateVerdict {
  ok: boolean;
  blockers: ReadyBlocker[];
  /** Ready-to-render tooltip. Empty string when the gate passes. */
  tooltip: string;
}

function blockerFor(variant: VariantState): ReadyBlocker | null {
  if (variant.isGenerating) {
    return {
      reason: 'generating',
      variantId: variant.id,
      aspectRatio: variant.aspectRatio,
      message: `${variant.aspectRatio} is still generating.`,
    };
  }
  if (!variant.mediaId) {
    return {
      reason: 'no-image',
      variantId: variant.id,
      aspectRatio: variant.aspectRatio,
      message: `${variant.aspectRatio} has no image yet.`,
    };
  }
  if (variant.needsReview) {
    return {
      reason: 'needs-review',
      variantId: variant.id,
      aspectRatio: variant.aspectRatio,
      message: `${variant.aspectRatio} still needs review after the last change.`,
    };
  }
  return null;
}

export function evaluateReadyGate(doc: VariantSetDocument | null): ReadyGateVerdict {
  if (!doc || doc.variants.length === 0) {
    const blockers: ReadyBlocker[] = [
      {
        reason: 'no-variants',
        variantId: null,
        aspectRatio: null,
        message: 'This concept has no sizes yet.',
      },
    ];
    return { ok: false, blockers, tooltip: blockers[0].message };
  }

  const blockers = [...doc.variants]
    .sort((a, b) => a.position - b.position)
    .map(blockerFor)
    .filter((b): b is ReadyBlocker => b !== null);

  return {
    ok: blockers.length === 0,
    blockers,
    tooltip: blockers.map((b) => b.message).join(' '),
  };
}

/** Hard refusal used by the store, so `Ready` cannot be set past the gate. */
export function assertReady(doc: VariantSetDocument | null): ReadyGateVerdict {
  return evaluateReadyGate(doc);
}
